<template>
  <div class="cropper-wrapper">
    <div v-if="!image" class="cropper-loading">
      <BbLoadingIndicator />
    </div>
    <template v-else>
      <Cropper ref="cropperRef" class="cropper" :src="image" :stencil-props="stencilProps" @change="onCropChange" />
      <div class="aspect-ratio-buttons">
        <BbBtn
          v-for="ratio in aspectRatios"
          :key="ratio.label"
          :type="selectedAspectRatio === ratio.label ? 'primary' : 'secondary'"
          size="small"
          @click="setAspectRatio(ratio)"
        >
          {{ ratio.label }}
        </BbBtn>
      </div>
    </template>
    <div class="cropper-actions">
      <BbBtn type="secondary" @click="closeModal">{{ t('common.cancel') }}</BbBtn>
      <BbBtn type="primary" @click="handleSave" :disabled="!image">{{ t('common.save') }}</BbBtn>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Cropper } from 'vue-advanced-cropper';
import { useRoute } from 'vue-router';
import { useWesley } from '@anthill-modules/wesley-components';
import 'vue-advanced-cropper/dist/style.css';

import useDocuments from '~/composables/useDocuments';
import {
  extractImageExternalId,
  extractUserImageFolderName,
  getDocumentKeyForUserImage,
  extractUserImageName,
} from '~/utils/editor-utils';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useImageGeneration from '~/composables/useImageGeneration';

const props = defineProps({
  imageSrc: {
    type: String,
    required: true,
  },
  cssPath: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['save', 'cancel']);

const { t } = useI18n();
const route = useRoute();

const { getAuthoredDocumentThumbs, getAuthoredUserImages } = useDocuments();
const { sendMessage: sendMessageToDocument } = useWesley();
const { saveHtmlDocumentContentToDb, uploadAuthoredImage, renderCitationTooltipsInIframe } = useDocumentEditor();

const cropperRef = ref(null);
const croppedCanvas = ref(null);
const image = ref(null);
const isUserImage = ref(false);
const selectedAspectRatio = ref('Free');

const aspectRatios = [
  { label: 'Free', value: null },
  { label: '1:1', value: 1 / 1 },
  {
    label: '4:3',
    value: 4 / 3,
  },
  { label: '16:9', value: 16 / 9 },
  { label: '3:4', value: 3 / 4 },
  { label: '9:16', value: 9 / 16 },
  {
    label: '3:1',
    value: 3 / 1,
  },
  { label: '6:1', value: 6 / 1 },
];

const stencilProps = computed(() => {
  const selected = aspectRatios.find((r) => r.label === selectedAspectRatio.value);
  if (selected?.value) {
    return { aspectRatio: selected.value };
  }
  return {};
});

const setAspectRatio = (ratio) => {
  selectedAspectRatio.value = ratio.label;
};

const onCropChange = ({ canvas }) => {
  croppedCanvas.value = canvas;
};

const handleSave = () => {
  if (croppedCanvas.value) {
    croppedCanvas.value.toBlob((blob) => {
      if (blob) {
        handleCroppedImage(blob);
      }
    }, 'image/png');
  }
};

const handleCroppedImage = async (croppedImageBlob) => {
  if (!croppedImageBlob || !props.cssPath) return;

  try {
    let documentId = extractImageExternalId(image.value);
    if (isUserImage.value) {
      // For user images, we need a different handling to get the document ID
      // Assuming user images have a different way to get their document key
      // Here we just use the image URL as a placeholder
      documentId = getDocumentKeyForUserImage(image.value);
    }
    const permanentImageUrl = await uploadAuthoredImage({
      authDocumentId: route.params.conversationId,
      documentId,
      croppedImage: croppedImageBlob,
      contentType: 'image/png',
    });

    await sendMessageToDocument({
      method: 'updateAttribute',
      params: {
        value: permanentImageUrl,
        attr: 'src',
      },
    });

    closeModal();
    setTimeout(() => {
      renderCitationTooltipsInIframe();
      saveHtmlDocumentContentToDb();
    }, 1000);
  } catch (error) {
    console.error('Error uploading cropped image:', error);
  }
};

const closeModal = () => {
  emit('cancel');
};

const getFreshOriginalImageUrl = async (externalId) => {
  const imagesFromBE = await getAuthoredDocumentThumbs([externalId], true);
  const originalImage = imagesFromBE?.find((img) => img.variant === 'original');
  return originalImage?.url || null;
};

const getFreshUserImageUrl = async (userImageKey) => {
  // Normalize to a plain user image id (UUID). userImageKey may be like:
  // - "users/<uuid>/..."
  // - "<orgId>/images/users/<uuid>/original-<uuid>.png"
  // - or already the UUID
  let userImageId = extractUserImageFolderName(userImageKey);

  if (!userImageId) {
    userImageId = extractUserImageName(userImageKey);
  }

  if (!userImageId) return null;

  const imagesFromBE = await getAuthoredUserImages(route.params.conversationId, [userImageId]);
  const originalImage = imagesFromBE?.find((img) => img.url.includes('original'));
  return originalImage?.url || null;
};

onMounted(async () => {
  const imageExternalId = extractImageExternalId(props.imageSrc);

  if (!imageExternalId) {
    const userImageKey = getDocumentKeyForUserImage(props.imageSrc);

    if (userImageKey) {
      image.value = await getFreshUserImageUrl(userImageKey);
      isUserImage.value = true;
    }
  } else {
    image.value = await getFreshOriginalImageUrl(imageExternalId);
  }

  if (!image.value) {
    console.error('Could not get original image URL for:', imageExternalId);
  }
});
</script>

<style scoped lang="scss">
.cropper-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.aspect-ratio-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: $bb-spacing-2;
  padding-bottom: $bb-spacing-2;
}

.cropper {
  height: 500px;
  background: #f5f5f5;
}

.cropper-loading {
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.cropper-actions {
  display: flex;
  justify-content: flex-end;
  gap: $bb-spacing-3;
  padding-top: $bb-spacing-2;
}
</style>
